import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Menu, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';
import { SidebarContent } from './SidebarContent';

export interface NavItem {
  label: string;
  href: string;
  icon: LucideIcon;
  active?: boolean;
  /** Sidebar section label, e.g. "Daily work". Ungrouped items sit at the top. */
  group?: string;
  /** Count shown on the item (e.g. things waiting). Hidden when 0. */
  badge?: number;
  /** Sidebar only: pinned to the bottom next to the user. */
  pinned?: boolean;
}

interface MobileShellProps {
  navItems: NavItem[];
  children: ReactNode;
  title?: string;
  actions?: ReactNode;
  breadcrumb?: ReactNode;
  banner?: ReactNode;
  churchName?: string;
  user?: { name: string; role: string };
  /** 'tabs' = bottom tab bar; 'drawer' = hamburger button opening a side drawer. */
  navStyle?: 'tabs' | 'drawer';
  onNavigate?: (href: string) => void;
}

export const MobileShell = ({
  navItems,
  children,
  title,
  actions,
  breadcrumb,
  banner,
  churchName,
  user,
  navStyle = 'tabs',
  onNavigate,
}: MobileShellProps) => {
  const drawer = navStyle === 'drawer';
  const [open, setOpen] = useState(false);
  const panel = useRef<HTMLDivElement>(null);
  const waiting = navItems.reduce((n, i) => n + (i.badge ?? 0), 0);

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panel.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', onKey);
      previouslyFocused?.focus?.();
    };
  }, [open]);

  const handleNavClick = (href: string, e: React.MouseEvent<HTMLAnchorElement>) => {
    if (onNavigate) {
      e.preventDefault();
      onNavigate(href);
    }
  };

  // A tab bar holds five. Anything beyond that (Account, for church staff) lives in the avatar menu.
  const displayNavItems = navItems.slice(0, 5);

  return (
    <div className="flex flex-col h-dvh bg-page">
      {/* Top Bar */}
      <div className="no-print sticky top-0 z-40 bg-surface border-b border-line" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="px-4 py-3 flex items-center justify-between gap-3">
          {drawer && (
            <button
              type="button"
              aria-label="Open menu"
              aria-haspopup="dialog"
              aria-expanded={open}
              onClick={() => setOpen(true)}
              className="relative -ml-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-control text-ink hover:bg-neutral-tint"
            >
              <Menu size={22} aria-hidden />
              {waiting > 0 && <span className="absolute right-1.5 top-1.5 h-2.5 w-2.5 rounded-full bg-wait" aria-hidden />}
            </button>
          )}
          <div className="min-w-0 flex-1">
            {breadcrumb}
            {title && <h1 className="truncate text-section font-semibold text-ink">{title}</h1>}
          </div>
          {actions && <div className="shrink-0">{actions}</div>}
        </div>
      </div>

      {banner && <div className="border-b border-line bg-page">{banner}</div>}

      {/* 16px side gutters, and room under the content for the fixed tab bar (bar is ~64px + the phone's safe area) */}
      <div data-page-scroller className="flex-1 overflow-y-auto px-4 pt-5" style={{ paddingBottom: drawer ? 'calc(2rem + env(safe-area-inset-bottom))' : 'calc(6rem + env(safe-area-inset-bottom))' }}>
        {children}
      </div>

      {/* Side drawer (when chosen) */}
      {drawer && open && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-ink/40" onClick={() => setOpen(false)} aria-hidden />
          <div
            ref={panel}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-label="Menu"
            className="absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col bg-surface shadow-xl outline-none"
            style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
          >
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => setOpen(false)}
              className="absolute right-2 top-3 flex h-10 w-10 items-center justify-center rounded-control text-muted hover:bg-neutral-tint"
            >
              <X size={20} aria-hidden />
            </button>
            <SidebarContent navItems={navItems} churchName={churchName} user={user} onNavigate={onNavigate} onItemChosen={() => setOpen(false)} />
          </div>
        </div>
      )}

      {/* Bottom Tab Bar */}
      {!drawer && (
      <nav
        className="fixed bottom-0 left-0 right-0 bg-surface border-t border-line z-40"
        role="navigation"
        aria-label="Main navigation"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="flex justify-around">
          {displayNavItems.map((item) => {
            const Icon = item.icon;
            return (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => handleNavClick(item.href, e)}
                className={cn(
                  'relative flex min-h-[56px] flex-1 flex-col items-center justify-center gap-1 px-1 py-2 text-micro font-medium transition-colors',
                  item.active ? 'text-brand-ink bg-brand-tint' : 'text-neutral hover:text-ink'
                )}
                aria-current={item.active ? 'page' : undefined}
              >
                <span className="relative">
                  <Icon size={24} aria-hidden />
                  {!!item.badge && (
                    <span
                      className="absolute -right-2.5 -top-1.5 min-w-4 rounded-full bg-wait px-1 text-center text-micro font-semibold leading-4 text-white"
                      aria-label={`${item.badge} waiting`}
                    >
                      {item.badge}
                    </span>
                  )}
                </span>
                <span>{item.label}</span>
              </a>
            );
          })}
        </div>
      </nav>
      )}
    </div>
  );
};
